import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export const Route = createFileRoute("/admin/login")({
  head: () => ({
    meta: [
      { title: "Админ панел — FIXHEN" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AdminLogin,
});

function AdminLogin() {
  const [email, setEmail] = useState("");
  const [pwd, setPwd] = useState("");
  const [mode, setMode] = useState<"magic" | "password">("magic");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const navigate = useNavigate();

  // Bounce already-authorised staff/admin straight into the panel.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: userRes } = await supabase.auth.getUser();
      if (cancelled || !userRes?.user) return;
      const { data: roles } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", userRes.user.id);
      const list = (roles ?? []).map((r) => r.role);
      if (list.includes("admin") || list.includes("staff")) {
        navigate({ to: "/admin" as any, replace: true });
      }
    })().catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    setMsg(null);
    try {
      if (mode === "magic") {
        const { error } = await supabase.auth.signInWithOtp({
          email,
          options: { emailRedirectTo: `${window.location.origin}/admin` },
        });
        if (error) throw error;
        setMsg("Ако имейлът е оторизиран, ще получите линк за вход.");
      } else {
        const { error } = await supabase.auth.signInWithPassword({ email, password: pwd });
        if (error) throw error;
        navigate({ to: "/admin" as any });
      }
    } catch (e: any) {
      setErr(e?.message ?? "Възникна грешка");
    } finally {
      setBusy(false);
    }
  }


  return (
    <div lang="bg" dir="ltr" className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
      <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
        <div className="mb-6 text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-[#172554] px-3 py-1 text-xs font-semibold text-white">
            FIXHEN · ADMIN
          </div>
          <h1 className="mt-4 text-2xl font-bold text-slate-900">Вход в админ панела</h1>
          <p className="mt-1 text-sm text-slate-500">
            Само за оторизирани служители на FIXHEN.
          </p>
        </div>
        <div className="mb-4 flex gap-1 rounded-lg bg-slate-100 p-1 text-sm">
          <button
            type="button"
            onClick={() => setMode("magic")}
            className={`flex-1 rounded-md px-3 py-1.5 font-medium ${mode === "magic" ? "bg-white shadow text-slate-900" : "text-slate-500"}`}
          >
            Magic link
          </button>
          <button
            type="button"
            onClick={() => setMode("password")}
            className={`flex-1 rounded-md px-3 py-1.5 font-medium ${mode === "password" ? "bg-white shadow text-slate-900" : "text-slate-500"}`}
          >
            Парола
          </button>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="email">Имейл</Label>
            <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@fixhen.com" />
          </div>
          {mode === "password" && (
            <div>
              <Label htmlFor="pwd">Парола</Label>
              <Input id="pwd" type="password" required value={pwd} onChange={(e) => setPwd(e.target.value)} />
            </div>
          )}
          {msg && <p className="rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-800">{msg}</p>}
          {err && <p className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-800">{err}</p>}
          <Button type="submit" disabled={busy} className="w-full bg-[#2563EB] hover:bg-[#1d4ed8]">
            {busy ? "Изпращане…" : mode === "magic" ? "Изпрати линк" : "Вход"}
          </Button>
        </form>
      </div>
    </div>
  );
}